import { z } from "zod";

// Skema untuk data bank di dalam properti
const BankSchema = z.object({
    id: z.string().uuid(),
    property_id: z.string().uuid(),
    account_name: z.string(),
    account_number: z.string(),
    bank_name: z.string(),
});

// Skema untuk data properti
const PropertySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    primary_email: z.string().email().nullable(),
    primary_phone: z.string().nullable(),
    primary_phone_country_code: z.string().nullable(),
    street_address: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    country: z.string().nullable(),
    postal_code: z.string().nullable(),
    website: z.string().url().nullable(),
    logo_url: z.string().url().nullable(),
    property_type: z.string().nullable(),
    checkin_time: z.string().nullable().optional(),
    checkout_time: z.string().nullable().optional(),
    banks: z.array(BankSchema).optional(),
});

// PERBAIKAN: Skema pembayaran dilengkapi dengan field dari data referensi
const PaymentSchema = z.object({
    id: z.string().uuid(),
    booking_room_id: z.string().uuid(),
    date_payment: z.string(),
    payment_type: z.string(),
    amount: z.coerce.number(),
    surcharge: z.coerce.number(),
    user_id: z.string().uuid(),
});

// Skema untuk tarif harian
const BookingRateSchema = z.object({
    id: z.number(),
    booking_room_id: z.string().uuid(),
    date: z.string(),
    rate: z.coerce.number(),
});

// Skema untuk booking extras
const BookingExtraSchema = z.object({
    id: z.string().uuid(),
    booking_room_id: z.string().uuid(),
    item_name: z.string(),
    // Rincian item + pajak dari POS — ditampilkan di bawah baris extra.
    description: z.string().nullable().optional(),
    qty: z.number(),
    amount: z.coerce.number(),
    subtotal: z.coerce.number(),
    date: z.string(),
    category: z.string().nullable(),
    source: z.string().nullable(),
});

// PERBAIKAN: Skema untuk satu kamar dilengkapi dengan semua field yang ada
const BookingRoomSchema = z.object({
    id: z.string().uuid(),
    booking_id: z.string().uuid(), // ID grup reservasi
    room_type_id: z.string().uuid(),
    room_id: z.string().uuid().nullable(),
    agent_id: z.string().uuid().nullable(),
    rate_plan_id: z.string().uuid(),
    checkin_date: z.string(),
    checkout_date: z.string(),
    nights: z.number(),
    adults: z.number(),
    children: z.number(),
    total_guests: z.number(),
    status: z.string(),
    mark: z.string().nullable(),
    invoice_number: z.string(),
    invoice_date: z.string(),
    public_note: z.string().nullable(),
    private_note: z.string().nullable(),
    customer_name: z.string(),
    customer_surname: z.string().nullable(),
    customer_email: z.string().nullable(),
    customer_phone: z.string().nullable(),
    customer_address: z.string().nullable(),
    customer_country: z.string().nullable(),
    customer_state: z.string().nullable(),
    customer_city: z.string().nullable(),
    customer_zip: z.string().nullable(),
    customer_date_of_birth: z.string().nullable(),
    customer_identity_number: z.string().nullable(),
    payment_type: z.string().nullable(),
    source: z.string(),
    total_rate: z.coerce.number(),
    total_payment: z.coerce.number().nullable(),
    total_surcharge: z.coerce.number().nullable(),
    due_payment: z.coerce.number(),
    booking_rates: z.array(BookingRateSchema),
    booking_extras: z.array(BookingExtraSchema).optional(),
    total_extras: z.coerce.number().nullable(),
    payments: z.array(PaymentSchema),
    // Relasi (opsional karena tergantung `include`)
    room_type: z.object({ id: z.string(), name: z.string() }).optional(),
    room: z.object({ id: z.string(), room_number: z.string() }).optional().nullable(),
    agent: z.object({ id: z.string(), name: z.string() }).optional().nullable(),
    rate_plan: z.object({ id: z.string(), name: z.string() }).optional(),
});

// Skema untuk objek 'data' utama
const ShowInvoiceDataSchema = z.object({
    property: PropertySchema,
    booking: BookingRoomSchema.optional(),
    bookingRoom: z.array(BookingRoomSchema).optional(),
    invoice_total: z.coerce.number(),
    due_payment: z.coerce.number(),
    payments: z.array(PaymentSchema).optional(),
});

// Skema utama untuk seluruh respons API
export const ShowInvoiceResponseV4Schema = z.object({
    success: z.boolean(),
    message: z.string(),
    code: z.number(),
    data: ShowInvoiceDataSchema,
});

export type ShowInvoiceResponseV4 = z.infer<typeof ShowInvoiceResponseV4Schema>;
export type ShowInvoiceDataV4 = z.infer<typeof ShowInvoiceDataSchema>;